import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Modal, Spin, Tag, message } from 'antd';
import {
  LeftOutlined, MoreOutlined, LaptopOutlined, PrinterOutlined,
  CoffeeOutlined, CarOutlined, ExperimentOutlined, ToolOutlined,
  AppstoreOutlined, SwapOutlined, FormOutlined, CameraOutlined, DeleteOutlined, EditOutlined,
  LeftCircleOutlined, RightCircleOutlined,
} from '@ant-design/icons';
import api from '../api';
import AssetPhotoPicker from '../components/AssetPhotoPicker';
import {
  ASSET_PHOTO_MAX_COUNT,
  movePhotoInList,
  prepareAssetPhotoFile,
  setPhotoAsCover,
} from '../utils/assetPhoto';
import { getStoredUser, hasPerm, isSuperAdmin as checkSuperAdmin } from '../userAuth';
import { H5_TAB_BAR_PX } from '../h5Layout';
import { assetListPathFromQs, getAssetListReturnQs } from '../utils/assetListNav';
import dayjs from 'dayjs';

const TYPE_STYLES = {
  '电子设备': { color: '#1677FF', bg: '#E8F0FE', icon: <LaptopOutlined /> },
  '办公设备': { color: '#52c41a', bg: '#E8F8E0', icon: <PrinterOutlined /> },
  '办公家具': { color: '#fa8c16', bg: '#FFF3E0', icon: <CoffeeOutlined /> },
  '交通工具': { color: '#722ed1', bg: '#F3E8FF', icon: <CarOutlined /> },
  '实验设备': { color: '#eb2f96', bg: '#FFE8F0', icon: <ExperimentOutlined /> },
  '维修工具': { color: '#13c2c2', bg: '#E0F8F8', icon: <ToolOutlined /> },
};
const DEFAULT_TYPE = { color: '#8c8c8c', bg: '#f0f0f0', icon: <AppstoreOutlined /> };

function getTypeStyle(type) {
  return TYPE_STYLES[type] || DEFAULT_TYPE;
}

function StatusTag({ text }) {
  const colorMap = {
    '使用中': { color: '#52c41a', bg: '#E8F8E0' },
    '折旧中': { color: '#1677FF', bg: '#E8F0FE' },
    '折旧完成': { color: '#8c8c8c', bg: '#f0f0f0' },
    '闲置': { color: '#fa8c16', bg: '#FFF3E0' },
  };
  const s = colorMap[text] || { color: '#666', bg: '#f0f0f0' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 4,
      fontSize: 12, color: s.color, background: s.bg, fontWeight: 500,
    }}>
      {text}
    </span>
  );
}

function InfoItem({ label, value, valueStyle }) {
  const inner = React.isValidElement(value)
    ? value
    : (value === undefined || value === null || value === '' ? '-' : value);
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#333', fontWeight: 400, ...valueStyle,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {inner}
      </div>
    </div>
  );
}

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const replaceInputRef = useRef(null);
  const replacePhotoIdRef = useRef(null);
  const user = getStoredUser();
  const canEditPhotos = hasPerm(user, 'asset_photo');
  const canApplyScrap = hasPerm(user, 'apply_scrap');
  const isSuperAdmin = checkSuperAdmin(user);
  const fromDisposedMode = String(searchParams.get('disposed_only') || '') === '1';
  const fromDisposalCenter = String(searchParams.get('from') || '') === 'disposal';
  const goBackToList = () => {
    if (fromDisposalCenter) {
      navigate('/disposal');
      return;
    }
    navigate(assetListPathFromQs(getAssetListReturnQs() || searchParams.toString()));
  };
  const photos = useMemo(() => (Array.isArray(asset?.photos) ? asset.photos : []), [asset?.photos]);

  const reloadAsset = () => {
    setLoading(true);
    api.get(`/assets/${id}`)
      .then(res => setAsset(res))
      .catch(() => message.error('加载失败'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reloadAsset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const uploadPhoto = async (rawFile) => {
    if (!rawFile) return;
    if (photos.length >= ASSET_PHOTO_MAX_COUNT) {
      message.warning(`每个资产最多 ${ASSET_PHOTO_MAX_COUNT} 张照片`);
      return;
    }
    setPhotoLoading(true);
    try {
      const file = await prepareAssetPhotoFile(rawFile);
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/assets/${id}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success('照片已上传并设为封面，可用左右箭头调整顺序');
      reloadAsset();
    } catch (err) {
      message.error(err.message || '上传失败');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleReplacePhoto = async (photoId, rawFile) => {
    if (!rawFile) return;
    setPhotoLoading(true);
    try {
      const file = await prepareAssetPhotoFile(rawFile);
      const fd = new FormData();
      fd.append('file', file);
      await api.put(`/assets/${id}/photos/${photoId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success('照片已替换');
      reloadAsset();
    } catch (err) {
      message.error(err.message || '替换失败');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    setPhotoLoading(true);
    try {
      await api.delete(`/assets/${id}/photos/${photoId}`);
      message.success('照片已删除');
      reloadAsset();
    } catch (err) {
      message.error(err.message || '删除失败');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleReorder = async (newOrder) => {
    const prev = asset;
    setAsset((a) => (a ? { ...a, photos: newOrder, cover_photo_url: newOrder[0]?.url || '' } : a));
    setPhotoLoading(true);
    try {
      await api.put(`/assets/${id}/photos/order`, { photo_ids: newOrder.map((p) => p.id) });
      message.success('封面/顺序已更新');
      reloadAsset();
    } catch (err) {
      setAsset(prev);
      message.error(err.message || '排序保存失败');
    } finally {
      setPhotoLoading(false);
    }
  };

  const movePhoto = (photoId, delta) => {
    const idx = photos.findIndex((p) => p.id === photoId);
    if (idx < 0) return;
    const nextIdx = idx + delta;
    if (nextIdx < 0 || nextIdx >= photos.length) return;
    handleReorder(movePhotoInList(photos, photoId, nextIdx));
  };

  const makeCover = (photoId) => {
    if (!photos.length || photos[0]?.id === photoId) return;
    handleReorder(setPhotoAsCover(photos, photoId));
  };

  const ts = asset ? getTypeStyle(asset.asset_type) : DEFAULT_TYPE;
  const depStatusText = asset?.status_text
    ?? (Number(asset?.net_value) <= 0 ? '折旧完成' : '折旧中');
  const scrapEligible = Number(asset?.net_value || 0) === 0;
  const isDisposedAsset = Boolean(asset?.is_disposed);
  const disposalStatus = Number(asset?.disposal_status);

  const handleDirectDispose = () => {
    if (!scrapEligible) {
      message.warning('当前资产未完成折旧（净值不为零），不能提交处置');
      return;
    }
    Modal.confirm({
      title: '提交至未上报',
      content: '将把该资产提交到处置「未上报」，之后需在处置中心完成上报与审核。',
      okText: '确认提交',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.post(`/assets/${id}/dispose`, { remark: '超级管理员提交未上报' });
          message.success('已提交至未上报');
          goBackToList();
        } catch (err) {
          message.error(err.message || '提交失败');
        }
      },
    });
  };

  const handleApplyWithdraw = () => {
    if (!canApplyScrap || isSuperAdmin) return;
    if (!isDisposedAsset) {
      message.warning('仅已处置资产可申请撤回');
      return;
    }
    Modal.confirm({
      title: '确认申请撤回',
      content: '将提交该资产的处置撤回申请，等待超级管理员审核，是否继续？',
      okText: '确认提交',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.post('/applies', {
            apply_type: 4,
            asset_ids: [Number(id)],
            remark: '单条申请撤回处置',
          });
          message.success('撤回申请已提交');
          goBackToList();
        } catch (err) {
          message.error(err.message || '提交撤回申请失败');
        }
      },
    });
  };

  const handleDeleteDisposed = () => {
    if (!isSuperAdmin || !isDisposedAsset) return;
    Modal.confirm({
      title: '确认删除',
      content: '将从在册列表移除该资产（软删除，保留日志）。若已「处置已完成」则不可删除，仅留存记录。',
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await api.delete(`/assets/${id}`);
          message.success('已删除');
          goBackToList();
        } catch (err) {
          message.error(err.message || '删除失败');
        }
      },
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f7fa',
      maxWidth: 750,
      margin: '0 auto',
      paddingBottom: `calc(${H5_TAB_BAR_PX}px + 72px + env(safe-area-inset-bottom, 0px))`,
    }}
    >
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', height: 48, borderBottom: '1px solid #f0f0f0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onClick={goBackToList}>
          <LeftOutlined style={{ fontSize: 16 }} />
          <span style={{ fontSize: 17, fontWeight: 600 }}>资产详情</span>
        </div>
        <MoreOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
      </div>

      <Spin spinning={loading || photoLoading}>
        {asset && (
          <>
            {/* Top section - icon + name + status */}
            <div style={{
              background: '#fff', padding: '24px 16px', marginBottom: 10,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <div style={{
                width: 80, height: 80, borderRadius: 16, background: ts.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14,
                overflow: 'hidden',
              }}>
                {photos[0]?.url ? (
                  <img
                    src={photos[0].url}
                    alt={asset.asset_name || '资产照片'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span style={{ fontSize: 40, color: ts.color }}>{ts.icon}</span>
                )}
              </div>
              {canEditPhotos && (
                <div
                  onClick={() => {
                    if (photos.length >= ASSET_PHOTO_MAX_COUNT) {
                      message.warning(`每个资产最多 ${ASSET_PHOTO_MAX_COUNT} 张照片`);
                      return;
                    }
                    setPickerOpen(true);
                  }}
                  style={{ marginTop: -6, marginBottom: 10, fontSize: 12, color: '#1677FF', cursor: 'pointer' }}
                >
                  <CameraOutlined /> 添加照片（最多{ASSET_PHOTO_MAX_COUNT}张）
                </div>
              )}
              <input
                ref={replaceInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (replacePhotoIdRef.current) handleReplacePhoto(replacePhotoIdRef.current, f);
                  replacePhotoIdRef.current = null;
                }}
              />
              <div style={{ fontSize: 20, fontWeight: 700, color: '#222', marginBottom: 8, textAlign: 'center' }}>
                {asset.asset_name}
              </div>
              <StatusTag text={depStatusText} />
              {asset?.disposal_reported ? (
                <div style={{
                  marginTop: 10, padding: '8px 10px', borderRadius: 8,
                  background: '#fff7e6', border: '1px solid #ffd591',
                  color: '#d46b08', fontSize: 13, fontWeight: 600,
                }}>
                  该资产已进行处置申请！
                </div>
              ) : disposalStatus === 2 ? (
                <div style={{
                  marginTop: 10, padding: '8px 10px', borderRadius: 8,
                  background: '#fff1f0', border: '1px solid #ffa39e',
                  color: '#cf1322', fontSize: 13, fontWeight: 600,
                }}>
                  已处置未完成（仍在资产列表，完成后方软删除）
                </div>
              ) : disposalStatus === 0 ? (
                <div style={{ marginTop: 8, color: '#fa8c16', fontSize: 12 }}>处置未上报</div>
              ) : asset?.pending_dispose && !isDisposedAsset ? (
                <div style={{ marginTop: 8, color: '#fa8c16', fontSize: 12 }}>处置申请审核中</div>
              ) : fromDisposedMode && disposalStatus === 3 ? (
                <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>已处置已完成（软删除留存）</div>
              ) : null}

              {photos.length > 0 && (
                <div style={{ width: '100%', marginTop: 14 }}>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                    资产照片（{photos.length}/{ASSET_PHOTO_MAX_COUNT}）
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
                    第一张为首图封面；点左右箭头或「封面」可换位置
                  </div>
                  <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                    {photos.map((p, idx) => (
                      <div
                        key={p.id}
                        style={{ position: 'relative', width: 88, flex: '0 0 auto' }}
                      >
                        <div style={{ position: 'relative', width: 88, height: 88 }}>
                          <img
                            src={p.url}
                            alt="资产照片"
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: 8,
                              objectFit: 'cover',
                              border: idx === 0 ? '2px solid #1677FF' : '1px solid #f0f0f0',
                            }}
                          />
                          {idx === 0 && (
                            <Tag
                              color="blue"
                              style={{
                                position: 'absolute', top: 4, left: 4, margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px',
                              }}
                            >
                              封面
                            </Tag>
                          )}
                        </div>
                        {canEditPhotos && (
                          <div style={{ marginTop: 4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                              <Button
                                size="small"
                                type="text"
                                icon={<LeftCircleOutlined />}
                                disabled={idx === 0}
                                onClick={() => movePhoto(p.id, -1)}
                              />
                              <Button
                                size="small"
                                type="link"
                                disabled={idx === 0}
                                style={{ padding: 0, fontSize: 11, height: 24 }}
                                onClick={() => makeCover(p.id)}
                              >
                                封面
                              </Button>
                              <Button
                                size="small"
                                type="text"
                                icon={<RightCircleOutlined />}
                                disabled={idx === photos.length - 1}
                                onClick={() => movePhoto(p.id, 1)}
                              />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                              <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => {
                                  replacePhotoIdRef.current = p.id;
                                  replaceInputRef.current?.click();
                                }}
                              />
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeletePhoto(p.id)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 基本信息 */}
            <div style={{
              background: '#fff', margin: '0 0 10px', padding: '16px',
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 14 }}>基本信息</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                <InfoItem label="资产编号" value={asset.asset_no || asset.asset_code} />
                <InfoItem label="清查编号" value={asset.check_code} />
                <InfoItem label="类别" value={asset.asset_type || asset.category_name} />
                <InfoItem label="数量" value={asset.quantity} />
                <InfoItem label="品牌" value={asset.brand} />
                <InfoItem label="型号" value={asset.model || asset.spec} />
              </div>
            </div>

            {/* 财务信息 */}
            <div style={{
              background: '#fff', margin: '0 0 10px', padding: '16px',
            }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>财务信息</div>
                <div style={{ fontSize: 11, color: '#bbb', marginTop: 2 }}>折旧按入账日期自动计算，每月1日后台更新</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
                <InfoItem label="单价"
                  value={asset.unit_price != null ? `¥${asset.unit_price}` : '-'} />
                <InfoItem label="入账日期"
                  value={asset.accounting_date ? dayjs(asset.accounting_date).format('YYYY-MM-DD') : '-'} />
                <InfoItem label="原值"
                  value={asset.original_value != null ? `¥${asset.original_value}` : '-'}
                  valueStyle={{ fontSize: 16, fontWeight: 700 }} />
                <InfoItem label="月折旧额"
                  value={asset.monthly_depreciation != null ? `¥${asset.monthly_depreciation}` : '-'}
                  valueStyle={{ color: '#ff4d4f' }} />
                <InfoItem label="已摊销金额"
                  value={asset.amortized_amount != null ? `¥${asset.amortized_amount}` : '-'} />
                <InfoItem label="当前净值"
                  value={asset.net_value != null ? `¥${asset.net_value}` : '-'}
                  valueStyle={{ fontSize: 16, fontWeight: 700, color: '#52c41a' }} />
                <InfoItem label="资产状态"
                  value={<StatusTag text={depStatusText} />} />
                <InfoItem label="总摊销月数" value={asset.total_month} />
                <InfoItem label="已摊销月数" value={asset.used_month} />
                <InfoItem label="剩余折旧月数"
                  value={asset.remaining_months}
                  valueStyle={{ color: '#52c41a', fontWeight: 700 }} />
              </div>
            </div>

            {/* 使用信息 */}
            <div style={{
              background: '#fff', margin: '0 0 10px', padding: '16px',
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 14 }}>使用信息</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>存放地点</div>
                  <div style={{ fontSize: 14, color: '#333', fontWeight: 600 }}>{asset.location_name || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>使用人</div>
                  <div style={{ fontSize: 14, color: '#333' }}>
                    {asset.user_name || '-'}
                    {asset.user_work_no ? ` (${asset.user_work_no})` : ''}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>取得日期</div>
                  <div style={{ fontSize: 14, color: '#333' }}>
                    {asset.acquire_date
                      ? dayjs(asset.acquire_date).format('YYYY-MM-DD')
                      : '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* 移动端不展示操作记录；仅后台超级管理员查看 */}
          </>
        )}
      </Spin>

      {/* Bottom buttons */}
      {asset && !isDisposedAsset && (
        <div style={{
          position: 'fixed',
          bottom: H5_TAB_BAR_PX,
          left: 0,
          right: 0,
          zIndex: 90,
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
          padding: '10px 16px',
          paddingBottom: 'max(10px, env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          gap: 12,
          maxWidth: 750,
          margin: '0 auto',
        }}
        >
          <div onClick={() => navigate(`/apply-borrow?assetId=${asset.id}`, { state: { assetId: asset.id, assetName: asset.asset_name } })}
            style={{
              flex: 1, height: 44, borderRadius: 8, background: '#1677FF',
              color: '#fff', fontSize: 15, fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              cursor: 'pointer',
            }}>
            <SwapOutlined /> 申请借用
          </div>
          <div onClick={() => navigate(`/apply-change/${id}`)}
            style={{
              flex: 1, height: 44, borderRadius: 8, background: '#fff',
              color: '#1677FF', fontSize: 15, fontWeight: 500,
              border: '1px solid #1677FF',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              cursor: 'pointer',
            }}>
            <FormOutlined /> 申请变更
          </div>
          {canApplyScrap && !isSuperAdmin && (
            <div
              onClick={() => {
                if (!scrapEligible) {
                  message.warning('当前资产未完成折旧（净值不为零），不能申请处置');
                  return;
                }
                navigate(`/apply-scrap/${id}`);
              }}
              style={{
                flex: 1, height: 44, borderRadius: 8,
                background: scrapEligible ? '#fa8c16' : '#ffd8bf',
                color: '#fff', fontSize: 15, fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                cursor: 'pointer',
              }}
            >
              <DeleteOutlined /> 申请处置
            </div>
          )}
          {isSuperAdmin && (
            <div
              onClick={handleDirectDispose}
              style={{
                flex: 1, height: 44, borderRadius: 8,
                background: scrapEligible ? '#ff4d4f' : '#ffd6d6',
                color: '#fff', fontSize: 15, fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                cursor: 'pointer',
              }}
            >
              <DeleteOutlined /> 提交未上报
            </div>
          )}
        </div>
      )}
      {asset && isDisposedAsset && (
        <div style={{
          position: 'fixed',
          bottom: H5_TAB_BAR_PX,
          left: 0,
          right: 0,
          zIndex: 90,
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
          padding: '10px 16px',
          paddingBottom: 'max(10px, env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          gap: 12,
          maxWidth: 750,
          margin: '0 auto',
        }}
        >
          {!isSuperAdmin && canApplyScrap && (
            <div
              onClick={handleApplyWithdraw}
              style={{
                flex: 1, height: 44, borderRadius: 8,
                background: '#1677ff',
                color: '#fff', fontSize: 15, fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                cursor: 'pointer',
              }}
            >
              <DeleteOutlined /> 申请撤回
            </div>
          )}
          {isSuperAdmin && (
            <div
              onClick={handleDeleteDisposed}
              style={{
                flex: 1, height: 44, borderRadius: 8,
                background: '#ff4d4f',
                color: '#fff', fontSize: 15, fontWeight: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                cursor: 'pointer',
              }}
            >
              <DeleteOutlined /> 删除
            </div>
          )}
        </div>
      )}
      <AssetPhotoPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        disabled={photoLoading}
        onPicked={uploadPhoto}
      />
    </div>
  );
}
